import { useRef, useEffect, useLayoutEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { DBG_ENABLED } from '../../utils/dbgFlag';
import { sync, SC } from '../../utils/syncProbe';
import { markEvent, EV } from '../../utils/jankRecorder';
import type { UploadedBatch } from '../../utils/uploadedBatch';

// ---------------------------------------------------------------------------
// Sonda de consistência filamento x candles (?dbg=1). Veja src/utils/syncProbe.ts.
//
// Só LÊ o que a cena já tem. A condição DBG_ENABLED é uma constante de módulo (nunca muda entre renders),
// então a ordem dos hooks é estável; sem a flag o hook retorna antes de registrar qualquer coisa.
// Não chama setState. O useFrame roda DEPOIS dos useFrame de deslocamento e de escala da cena
// (ordem de registro), então lê os valores já aplicados no quadro.
// ---------------------------------------------------------------------------

export interface SyncProbeArgs {
  data: any[]; wallData: any[];
  sliceOffset: number; wallSliceOffset: number; padLeft: number; wallPadLeft: number;
  minP: number; maxP: number; wallMinP: number; wallMaxP: number;
  zScale: number; smoothedPoints: any; smoothedPtsCount: number; candlesBack: any;
  stepX: number; H: number; vis: number;
  historyOffsetRef: { current: number } | undefined;
  scaleStateRef: { current: { zScale: number } };
  autoRotateEnabled: boolean;
  camera: THREE.Camera;
  filGroupX: { current: THREE.Group | null }; filGroupYZ: { current: THREE.Group | null };
  candGroupX: { current: THREE.Group | null }; candGroupY: { current: THREE.Group | null };
  filLine: { current: THREE.Line | null };
  /** Lote que o useFrame da cena realmente usa para posicionar/escalar (C1). A sonda mede ESTE, não as props commitadas. */
  drawRef: { current: UploadedBatch | null };
}

export function useFilamentSyncProbe(a: SyncProbeArgs): void {
  if (!DBG_ENABLED) return;
  const P = sync!;
  const prev = useRef({ sliceF: a.sliceOffset, sliceC: a.wallSliceOffset, cx: 0, cy: 0, cz: 0, camInit: false });

  // Commit do React (layout effect = mesmo momento em que o useFrame passa a ver as props novas)
  useLayoutEffect(() => {
    const isLote = prev.current.sliceF !== a.sliceOffset;
    prev.current.sliceF = a.sliceOffset;
    const now = performance.now();
    P.commit(0, now, isLote);
    if (isLote) markEvent(EV.COMMIT_LOTE);
  }, [a.data]);
  useLayoutEffect(() => {
    const isLote = prev.current.sliceC !== a.wallSliceOffset;
    prev.current.sliceC = a.wallSliceOffset;
    const now = performance.now();
    P.commit(1, now, isLote);
    if (isLote) markEvent(EV.COMMIT_LOTE_PAREDE);
  }, [a.wallData]);

  // Upload dos buffers: efeito passivo da cena, que roda DEPOIS dos efeitos dos filhos (FastLine / InstancedCandles)
  useEffect(() => {
    P.uDataF = a.data; P.uPadF = a.padLeft; P.uMinF = a.minP; P.uMaxF = a.maxP; P.uZsF = a.zScale; P.uCntF = a.smoothedPtsCount; P.uSliceF = a.sliceOffset;
    P.uploaded(0, performance.now());
    markEvent(EV.UPLOAD_FIL);
  }, [a.smoothedPoints]);
  useEffect(() => {
    P.uDataC = a.wallData; P.uPadC = a.wallPadLeft; P.uMinC = a.wallMinP; P.uMaxC = a.wallMaxP; P.uSliceC = a.wallSliceOffset;
    P.uploaded(1, performance.now());
    markEvent(EV.UPLOAD_CAND);
  }, [a.candlesBack, a.wallMinP, a.wallMaxP]);

  // Um chamado por quadro, depois dos useFrame de deslocamento e escala
  useFrame(() => {
    const now = performance.now();
    // fonte do desenho (a mesma que o useFrame da cena usa); a diferença para as props commitadas = commit pendente
    const d = a.drawRef.current;
    if (d) { P.cDataF = d.data; P.cDataC = d.wallData; P.cSliceF = d.sliceOffset; P.cSliceC = d.wallSliceOffset; P.pendNowF = d.data !== a.data; P.pendNowC = d.wallData !== a.wallData; }
    else { P.cDataF = a.data; P.cDataC = a.wallData; P.cSliceF = a.sliceOffset; P.cSliceC = a.wallSliceOffset; P.pendNowF = false; P.pendNowC = false; }
    P.stepX = a.stepX; P.H = a.H; P.vis = a.vis;
    // distância do fim da vista (só classifica o modo "passado" da sonda); os deslocamentos esperados vêm dos lotes
    P.pos = a.historyOffsetRef ? a.historyOffsetRef.current : 0;
    const gx = a.filGroupX.current, gy = a.filGroupYZ.current, cx = a.candGroupX.current, cy = a.candGroupY.current;
    if (gx) P.gxF = gx.position.x;
    if (cx) P.gxC = cx.position.x;
    if (gy) { P.yAF = gy.scale.y; P.yCF = gy.position.y; P.zKF = gy.scale.z; }
    if (cy) { P.yAC = cy.scale.y; P.yCC = cy.position.y; }
    P.zsExp = a.scaleStateRef.current.zScale;
    const line = a.filLine.current;
    if (line) {
      const dr = line.geometry.drawRange.count;
      P.drawCount = isFinite(dr) ? dr : 0;
      P.drawVisible = line.visible;
      const attr = line.geometry.getAttribute('position');
      P.verF = attr ? (attr as THREE.BufferAttribute).version : 0;
    }
    P.verC = P.tot[SC.UPLOAD_CAND];
    // câmera mexendo? (rotação por gesto, auto-rotate ou zoom)
    const c = a.camera.position, pv = prev.current;
    const moved = Math.abs(c.x - pv.cx) + Math.abs(c.y - pv.cy) + Math.abs(c.z - pv.cz) > 1e-3;
    pv.cx = c.x; pv.cy = c.y; pv.cz = c.z;
    P.camMoving = pv.camInit ? moved : false; pv.camInit = true;
    P.autoRotateOn = a.autoRotateEnabled;
    P.frame(now);
  });
}
